/**
 * Navigation Utilities
 *
 * Helper functions for determining navigation paths based on user entitlements.
 * Used to redirect users to appropriate pages after login based on their permissions.
 */

import { ENTITLEMENT_ENTITIES } from "../constants/entitlements";

/**
 * Route configuration with required entity entitlements
 */
export interface RouteConfig {
  path: string;
  label: string;
  requiredEntities: (keyof typeof ENTITLEMENT_ENTITIES)[];
  priority: number; // Lower number = higher priority for landing page
}

/**
 * Available routes with their entitlement requirements
 * Ordered by priority - first matching route will be used as landing page
 */
export const ROUTE_CONFIGS: RouteConfig[] = [
  // Dashboard (Case Management) - requires Case access
  {
    path: "/",
    label: "Dashboard",
    requiredEntities: ["Case"],
    priority: 1,
  },
  // Case Management
  {
    path: "/cases",
    label: "Case Management",
    requiredEntities: ["Case"],
    priority: 2,
  },
  // Compliance Management - requires Document, Rule, or Job access
  {
    path: "/healthcare/files",
    label: "Document Library",
    requiredEntities: ["Document"],
    priority: 3,
  },
  {
    path: "/healthcare/rules",
    label: "Compliance Rules",
    requiredEntities: ["Rule"],
    priority: 4,
  },
  {
    path: "/healthcare/processing",
    label: "Processing Queue",
    requiredEntities: ["Job"],
    priority: 5,
  },
  // Subscriptions
  {
    path: "/subscriptions/plans",
    label: "Browse Plans",
    requiredEntities: ["SubscriptionPackage"],
    priority: 6,
  },
  {
    path: "/subscriptions/manage",
    label: "My Subscriptions",
    requiredEntities: ["Subscription"],
    priority: 7,
  },
  // User Management
  {
    path: "/users",
    label: "Users",
    requiredEntities: ["User"],
    priority: 8,
  },
  {
    path: "/roles",
    label: "Role Management",
    requiredEntities: ["Role"],
    priority: 9,
  },
  {
    path: "/admin/tenants",
    label: "Tenant Management",
    requiredEntities: ["Tenant"],
    priority: 10,
  },
  // System Administration
  {
    path: "/admin/document-types",
    label: "Document Types",
    requiredEntities: ["DocumentType"],
    priority: 11,
  },
  {
    path: "/admin/prompts",
    label: "Extraction Prompts",
    requiredEntities: ["Prompt"],
    priority: 12,
  },
  // Questionnaire
  {
    path: "/compliance/questionnaire",
    label: "Completed Questionnaire",
    requiredEntities: ["Questionnaire"],
    priority: 13,
  },
];

/**
 * Check if user has access to any entitlement within an entity
 *
 * @param entitlementIds - Array of user's entitlement IDs
 * @param entity - Entity name to check
 * @returns boolean - true if user has any entitlement for the entity
 */
export const hasEntityAccess = (
  entitlementIds: number[],
  entity: keyof typeof ENTITLEMENT_ENTITIES,
): boolean => {
  const entityIds = ENTITLEMENT_ENTITIES[entity];
  if (!entityIds) return false;
  return entityIds.some((id) => entitlementIds.includes(id));
};

/**
 * Check if user has access to a route based on required entities
 *
 * @param entitlementIds - Array of user's entitlement IDs
 * @param requiredEntities - Array of entity names that grant access (ANY required)
 * @param userRole - Optional user role for super admin check
 * @returns boolean - true if user can access the route
 */
export const canAccessRoute = (
  entitlementIds: number[],
  requiredEntities: (keyof typeof ENTITLEMENT_ENTITIES)[],
  userRole?: string,
): boolean => {
  // Super admin has access to everything
  if (userRole?.toLowerCase() === "superadmin") {
    return true;
  }

  // Check if user has ANY of the required entity entitlements
  return requiredEntities.some((entity) =>
    hasEntityAccess(entitlementIds, entity),
  );
};

/**
 * Get the best landing page for a user based on their entitlements
 *
 * @param entitlementIds - Array of user's entitlement IDs
 * @param userRole - Optional user role for super admin check
 * @returns string - The path to redirect to after login
 */
export const getBestLandingPage = (
  entitlementIds: number[],
  userRole?: string,
): string => {
  // Super admin goes to dashboard
  if (userRole?.toLowerCase() === "superadmin") {
    return "/";
  }

  // Find first accessible route by priority
  const sortedRoutes = [...ROUTE_CONFIGS].sort(
    (a, b) => a.priority - b.priority,
  );

  for (const route of sortedRoutes) {
    if (canAccessRoute(entitlementIds, route.requiredEntities, userRole)) {
      return route.path;
    }
  }

  // If user has no accessible routes, return a special "no-access" path
  // This will be handled by the dashboard to show an appropriate message
  return "/no-access";
};

/**
 * Check if user has any dashboard-worthy entitlement
 * Used to determine if user should see the dashboard at all
 *
 * @param entitlementIds - Array of user's entitlement IDs
 * @param userRole - Optional user role for super admin check
 * @returns boolean - true if user has any meaningful access
 */
export const hasAnyDashboardAccess = (
  entitlementIds: number[],
  userRole?: string,
): boolean => {
  // Super admin always has access
  if (userRole?.toLowerCase() === "superadmin") {
    return true;
  }

  // Check if user has any entitlement at all
  return entitlementIds.length > 0;
};
